import { Component, input, type InputSignal } from "@angular/core";

import { AuthArtComponent, type AuthArtName } from "../auth-art/auth-art.component";

/**
 * The split photo-panel layout every unauthenticated screen sits in — board 7.
 *
 * One component for the door, the no-access screen, the unknown-slug fallback
 * and the expired session, because they differ only in the column on the right.
 * Four copies of a 38/62 grid with a gradient scrim is how these four screens
 * would otherwise drift apart.
 *
 * The panel carries an original line drawing on a navy→blue gradient, with a
 * curved edge into the form column. It replaced a photographic panel on
 * 2026-09-26: no photography was ever supplied, `heroImageUrl` is null on every
 * branding record, so in practice that panel had always rendered as a flat
 * navy rectangle. A drawing we can author is better than a photo slot nobody
 * has filled.
 *
 * `imageUrl` is kept. A real photograph, when one exists, still wins over the
 * drawing — the scrim that made white type legible over it is kept for exactly
 * that case.
 *
 * SCOPE. The design system's Imagery rule confines the panel — drawing or
 * photograph — to the five unauthenticated screens. See `reference.md`,
 * amended 2026-09-26.
 */
@Component({
  selector: "app-auth-layout",
  standalone: true,
  imports: [AuthArtComponent],
  templateUrl: "./auth-layout.component.html",
  styleUrl: "./auth-layout.component.scss",
})
export class AuthLayoutComponent {

  /** The large statement on the panel. */
  public readonly headline: InputSignal<string> = input.required<string>();

  public readonly blurb: InputSignal<string> = input<string>("");

  /** Short pill tags along the panel's foot. */
  public readonly tags: InputSignal<string[]> = input<string[]>([]);

  /**
   * Optional photograph. Supplying one REPLACES the drawing — `art` is not
   * rendered at all — and brings the dark scrim with it, which is what keeps
   * white type legible over an arbitrary image.
   *
   * Null, the default, is the normal case today: no photography exists and
   * `heroImageUrl` is null on every branding record, so every screen draws
   * `art` on the gradient and no scrim is emitted.
   */
  public readonly imageUrl: InputSignal<string | null> = input<string | null>(null);

  /**
   * Which drawing the panel shows. Required, so a new screen cannot quietly
   * ship an empty panel — the previous `imageUrl`-only contract defaulted to
   * blank, which is how the photo slot stayed empty from the component's
   * creation to this change without anyone noticing.
   */
  public readonly art: InputSignal<AuthArtName> = input.required<AuthArtName>();

  /**
   * Tint the panel with the tenant accent instead of Davaco navy.
   *
   * Off for the staff door, which is Davaco's own screen. On for a customer
   * screen, where the panel is one of the surfaces the brand layer may reach.
   */
  public readonly branded: InputSignal<boolean> = input<boolean>(false);

  /**
   * Bring the panel to life: the gradient drifts slowly and the drawing plays
   * its loop (see AuthArtComponent.animated). Off by default; on for the
   * customer-host /no-access. Stops under `prefers-reduced-motion`.
   */
  public readonly animated: InputSignal<boolean> = input<boolean>(false);
}
